import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  StateKey,
  TransferState,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, defer, map, Observable, of, shareReplay } from 'rxjs';

import { ApiService } from '../../../shared/services/api.service';
import { withTransferState } from '../../../shared/utils/transfer-state.util';
import { UPCOMING_EVENTS_LIMITS } from '../constants/events.constants';
import { toStoreEvents } from './events.mappers';
import type {
  PublicEventsCalendarPayload,
  PublicGameEventsPayload,
  PublicUpcomingEventsPayload,
  StoreEvent,
} from './events.model';

function gameEventsKey(slug: string): StateKey<StoreEvent[]> {
  return makeStateKey<StoreEvent[]>(`perron.events.game.${slug}`);
}

function calendarKey(month: string, game?: string): StateKey<StoreEvent[]> {
  return makeStateKey<StoreEvent[]>(`perron.events.calendar.${month}.${game ?? 'all'}`);
}

function upcomingKey(limit: number): StateKey<StoreEvent[]> {
  return makeStateKey<StoreEvent[]>(`perron.events.upcoming.${limit}`);
}

/**
 * Cliente de los tres endpoints públicos de eventos
 * (`GET /api/web/games/{slug}/events`, `GET /api/web/events/calendar` y
 * `GET /api/web/events/upcoming`).
 *
 * Mismo patrón que `GameSectionService`: `providedIn:'root'`, `withTransferState`
 * para que el cliente hidrate con lo que ya pidió el render server, y
 * `shareReplay(1)` para que dos suscripciones a la misma URL no disparen dos
 * requests.
 *
 * **404 → lista vacía.** Un slug que no existe, una sección despublicada y una
 * borrada son la misma situación desde la tienda: no hay eventos que enseñar.
 * Se traduce a `[]` y no a un error para que quien llama no tenga que distinguir
 * entre "no hay" y "no existe" solo para pintar el mismo estado vacío.
 *
 * Los fallos **reales** (red caída, 500) sí se propagan: quien llama decide si
 * los degrada a estado vacío o los rotula como error. Lo que este servicio
 * garantiza es que nunca emite algo que no sea un `StoreEvent[]`.
 */
@Injectable({ providedIn: 'root' })
export class EventsService {
  private readonly api = inject(ApiService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly transferState = inject(TransferState);

  private readonly cachedByGame = new Map<string, Observable<StoreEvent[]>>();
  private readonly cachedCalendars = new Map<string, Observable<StoreEvent[]>>();
  private readonly cachedUpcoming = new Map<number, Observable<StoreEvent[]>>();

  /** Listado completo de eventos no terminados de un juego, en orden cronológico. */
  getByGame(slug: string): Observable<StoreEvent[]> {
    const cached = this.cachedByGame.get(slug);
    if (cached) return cached;

    const request$ = withTransferState(
      this.transferState,
      this.isBrowser,
      gameEventsKey(slug),
      defer(() =>
        this.api.get<PublicGameEventsPayload>(`/web/games/${slug}/events`).pipe(
          map((res) => toStoreEvents(res?.data)),
          catchError(notFoundToEmpty),
        ),
      ),
    ).pipe(shareReplay(1));

    this.cachedByGame.set(slug, request$);
    return request$;
  }

  /**
   * Eventos que **solapan** el mes (`YYYY-MM`), opcionalmente de un solo juego.
   * A diferencia del listado por juego incluye los ya celebrados dentro del mes:
   * un calendario que esconde los días pasados de su propio mes se ve roto.
   */
  getCalendar(month: string, game?: string): Observable<StoreEvent[]> {
    const cacheKey = `${month}|${game ?? ''}`;
    const cached = this.cachedCalendars.get(cacheKey);
    if (cached) return cached;

    const request$ = withTransferState(
      this.transferState,
      this.isBrowser,
      calendarKey(month, game),
      defer(() =>
        this.api
          .get<PublicEventsCalendarPayload>('/web/events/calendar', { month, game })
          .pipe(
            map((res) => toStoreEvents(res?.data)),
            catchError(notFoundToEmpty),
          ),
      ),
    ).pipe(shareReplay(1));

    this.cachedCalendars.set(cacheKey, request$);
    return request$;
  }

  /**
   * Próximos eventos de **toda la tienda**, en orden cronológico: los de
   * cualquier juego y también los que no tienen ninguno.
   *
   * Alimenta las tres superficies de comunidad —portada, `/comunidad` y el
   * relleno de la landing de juego— con una sola respuesta: el `limit` es
   * único y canónico (D10), así que las tres comparten entrada de caché en el
   * backend, clave de `TransferState` y petición dentro del paso de SSR.
   * Quien llama corta a lo suyo.
   *
   * El `limit` es parámetro por si aparece otro consumidor con otra necesidad
   * (el endpoint acepta hasta 24), pero el front usa un solo valor a propósito:
   * un límite variable fragmentaría el caché en una entrada por combinación.
   */
  getUpcoming(limit: number = UPCOMING_EVENTS_LIMITS.request): Observable<StoreEvent[]> {
    const cached = this.cachedUpcoming.get(limit);
    if (cached) return cached;

    const request$ = withTransferState(
      this.transferState,
      this.isBrowser,
      upcomingKey(limit),
      defer(() =>
        this.api
          .get<PublicUpcomingEventsPayload>('/web/events/upcoming', { limit })
          .pipe(
            map((res) => toStoreEvents(res?.data)),
            catchError(notFoundToEmpty),
          ),
      ),
    ).pipe(shareReplay(1));

    this.cachedUpcoming.set(limit, request$);
    return request$;
  }
}

/** 404 = juego inexistente o despublicado; desde la tienda es "no hay eventos". */
function notFoundToEmpty(err: unknown): Observable<StoreEvent[]> {
  if (err instanceof HttpErrorResponse && err.status === 404) {
    return of([]);
  }
  throw err;
}
